/**
 * ч.770: редактор «Депонування зарплати» — невидана у строк готівка з платіжної
 * відомості: Дт 661/663 — Кт 662 по працівнику (при проведенні). Зазвичай
 * створюється з відомості («ще дії → закрити відомість»), можна й вручну.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { salaryDeposits, type SalaryDepositCreate } from '@/api/salary_deposits';
import { orgs } from '@/api/catalogs';
import { employees as employeesApi } from '@/api/hr';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useConfirm } from '@/components/ConfirmProvider';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { MONTHS_UK } from '@/shared/components/PeriodFilter';
import { createThenPost } from '@/shared/utils/createThenPost';
import TchTable from '@/shared/components/TchTable';

interface Row { employee_id: string; amount: string; settlement_account_code: string }
interface FormState {
  organization_id: string; document_number: string; document_date: string;
  payroll_payment_id: string; period_year: string; period_month: string; comment: string; items: Row[];
}
const fmt2 = (v: number) => v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function SalaryDepositEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const now = new Date();
  const [form, setForm] = useState<FormState>({
    organization_id: '', document_number: '', document_date: now.toISOString().slice(0, 16),
    payroll_payment_id: '', period_year: String(now.getFullYear()), period_month: String(now.getMonth() + 1),
    comment: '', items: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'lines' | 'accounts'>('lines');
  // ч.882: попап вибору депонента для РКО (коли рядків більше одного).
  const [payoutPickerOpen, setPayoutPickerOpen] = useState(false);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('payroll.manage');
  const docQ = useQuery({ queryKey: ['salary-deposit', id], queryFn: () => salaryDeposits.get(id!), enabled: !isNew });
  const empQ = useQuery({
    queryKey: ['deposit-employees', form.organization_id],
    queryFn: () => employeesApi.list({ organization_id: form.organization_id, active_only: false, limit: 1000 }),
    enabled: !!form.organization_id,
  });
  const empName = useMemo(() => {
    const m = new Map<string, string>();
    empQ.data?.items.forEach((e) => m.set(e.id, e.full_name ?? e.code ?? e.id));
    return m;
  }, [empQ.data]);

  useEffect(() => {
    if (isNew) {
      setForm((f) => f.organization_id ? f : { ...f, organization_id: availableOrgs[0]?.id ?? '' });
    } else if (docQ.data) {
      const d = docQ.data;
      setForm({
        organization_id: d.organization_id, document_number: d.document_number,
        document_date: d.document_date.slice(0, 16), payroll_payment_id: d.payroll_payment_id ?? '',
        period_year: d.period_year ? String(d.period_year) : '', period_month: d.period_month ? String(d.period_month) : '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({ employee_id: it.employee_id, amount: it.amount, settlement_account_code: it.settlement_account_code ?? '' })),
      });
    }
  }, [isNew, docQ.data, availableOrgs]);

  const isPosted = !isNew && (docQ.data?.posted ?? false);
  const total = form.items.reduce((s, it) => s + Number(it.amount || 0), 0);
  const payload = (): SalaryDepositCreate => ({
    organization_id: form.organization_id,
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    payroll_payment_id: form.payroll_payment_id || null,
    period_year: form.period_year ? Number(form.period_year) : null,
    period_month: form.period_month ? Number(form.period_month) : null,
    comment: form.comment || null,
    items: form.items.filter((it) => it.employee_id && Number(it.amount) > 0).map((it) => ({
      employee_id: it.employee_id, amount: it.amount, settlement_account_code: it.settlement_account_code || null })),
  });
  const errText = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Помилка';
  const invalidate = () => qc.invalidateQueries({ queryKey: ['salary-deposit', id] });
  const create = useMutation({ mutationFn: () => salaryDeposits.create(payload()),
    onSuccess: (d) => navigate(`/salary-deposits/${d.id}`), onError: (e) => setError(errText(e)) });
  const update = useMutation({ mutationFn: () => salaryDeposits.update(id!, payload()),
    onSuccess: invalidate, onError: (e) => setError(errText(e)) });
  const post = useMutation({ mutationFn: async () => {
      // ч.1266: на новому документі — створити, провести, відкрити створений.
      if (isNew) {
        return createThenPost(
          () => salaryDeposits.create(payload()),
          (docId) => salaryDeposits.post(docId),
          (d) => navigate(`/salary-deposits/${d.id}`),
        );
      }
      await salaryDeposits.update(id!, payload());
      return salaryDeposits.post(id!);
    },
    onSuccess: invalidate, onError: (e) => setError(errText(e)) });
  const unpost = useMutation({ mutationFn: () => salaryDeposits.unpost(id!), onSuccess: invalidate, onError: (e) => setError(errText(e)) });
  const remove = useMutation({ mutationFn: () => salaryDeposits.remove(id!), onSuccess: () => navigate('/salary-deposits') });

  const updRow = (i: number, patch: Partial<Row>) =>
    setForm((f) => ({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));

  // ч.882 (запит власника): наступний крок потоку ч.770 — РКО «Виплата
  // депонованої ЗП» (DEPOSIT_PAYOUT, Дт 662 — Кт 301, по працівнику). Один
  // депонент → одразу РКО з працівником/сумою/підставою; кілька → попап вибору.
  // Відомість-підстава — у «Звʼязаних документах» (related_documents).
  const payoutBasis = `Виплата депонованої заробітної плати${form.period_month
    ? ` за ${MONTHS_UK[Number(form.period_month)]?.toLowerCase() ?? ''} ${form.period_year}` : ''}`;
  const goPayout = (employeeId: string, amount: string) => navigate(
    `/cash-payments/new?org=${form.organization_id}&op=DEPOSIT_PAYOUT&employee=${employeeId}`
    + `&amount=${encodeURIComponent(amount)}&purpose=${encodeURIComponent(payoutBasis)}`
    + `&party=${encodeURIComponent(empName.get(employeeId) ?? '')}`);
  const railActions = [{
    label: 'виплатити депонент (РКО)',
    // ч.1347: РКО — зі збереженого депонування, проведення не чекаємо.
    title: isNew ? 'Спершу збережіть документ'
      : form.items.length === 0 ? 'У документі немає депонентів'
      : 'РКО «Виплата депонованої ЗП» (Дт 662 — Кт 301) працівнику з цього документа',
    disabled: isNew || form.items.length === 0,
    onClick: () => {
      const only = form.items.length === 1 ? form.items[0] : undefined;
      if (only) goPayout(only.employee_id, only.amount);
      else setPayoutPickerOpen(true);
    },
  }];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'items', title: 'Депоненти', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{isNew ? 'Нове депонування зарплати' : `Депонування зарплати ${form.document_number}`}</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          isNew={isNew} isPosted={isPosted}
          onBackToList={() => navigate('/salary-deposits')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={!form.organization_id || create.isPending || update.isPending}
          savePending={create.isPending || update.isPending}
          onPost={() => post.mutate()} postPending={post.isPending}
          postDisabled={form.items.length === 0}
          onUnpost={() => unpost.mutate()} unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          entriesDoc={{ type: 'doc.salary_deposits', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>
      {error && <div className="error" style={{ marginBottom: '0.5rem' }}>{error}</div>}

      {payoutPickerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setPayoutPickerOpen(false)}>
          <div className="card" style={{ width: 520, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Кому виплатити депонент</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setPayoutPickerOpen(false)}>закрити</button>
            </div>
            <TchTable>
              <thead><tr><th>Працівник</th><th className="num" style={{ width: 120 }}>Сума</th><th style={{ width: 130 }}></th></tr></thead>
              <tbody>
                {form.items.map((it, i) => (
                  <tr key={i}>
                    <td>{empName.get(it.employee_id) ?? it.employee_id}</td>
                    <td style={{ textAlign: 'right' }}>{fmt2(Number(it.amount))}</td>
                    <td><button type="button" className="primary"
                      onClick={() => { setPayoutPickerOpen(false); goPayout(it.employee_id, it.amount); }}>
                      створити РКО</button></td>
                  </tr>
                ))}
              </tbody>
            </TchTable>
          </div>
        </div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.salary_deposits', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>№</label>
          <input value={form.document_number} disabled={isPosted} placeholder="(авто)"
            onChange={(e) => setForm({ ...form, document_number: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date} disabled={isPosted}
            onChange={(v) => setForm({ ...form, document_date: v })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Місяць нарахування</label>
          <select value={form.period_month} disabled={isPosted}
            onChange={(e) => setForm({ ...form, period_month: e.target.value })}>
            <option value="">—</option>
            {MONTHS_UK.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Рік</label>
          <input type="number" value={form.period_year} disabled={isPosted}
            onChange={(e) => setForm({ ...form, period_year: e.target.value })} />
        </div>
      </div>
      {form.payroll_payment_id && (
        <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
          На підставі відомості виплати:{' '}
          <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
            onClick={() => navigate(`/payroll-payments/${form.payroll_payment_id}`)}>відкрити</a>
        </div>
      )}
      </DocBlock>

      <DocBlock index={2} title="Депоненти"
        actions={!isPosted ? (
          <button type="button" className="primary"
            onClick={() => setForm((f) => ({ ...f, items: [...f.items, { employee_id: '', amount: '0', settlement_account_code: '' }] }))}>
            + додати рядок
          </button>
        ) : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          <button type="button" className={tab === 'lines' ? 'active' : ''} onClick={() => setTab('lines')}>
            Депоненти ({form.items.length})</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={tab === 'accounts' ? 'active' : ''} onClick={() => setTab('accounts')}>
            Рахунки обліку</button>
        </div>
      </div>

      {tab === 'lines' && (<>
        <TchTable>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Працівник</th>
              <th className="num" style={{ width: 140 }}>Сума</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {form.items.map((it, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>
                  {isPosted ? (empName.get(it.employee_id) ?? it.employee_id) : (
                    <SearchSelect
                      items={(empQ.data?.items ?? []).map((e) => ({ id: e.id, label: e.full_name ?? e.code ?? e.id,
                        hint: e.is_active === false ? 'звільнений' : (e.code ?? null) }))}
                      value={it.employee_id}
                      onChange={(eid) => updRow(i, { employee_id: eid })} />
                  )}
                </td>
                <td>
                  {isPosted ? <div style={{ textAlign: 'right' }}>{fmt2(Number(it.amount))}</div> : (
                    <input type="number" step="0.01" value={it.amount}
                      onChange={(e) => updRow(i, { amount: e.target.value })} />
                  )}
                </td>
                <td>{!isPosted && (
                  <button className="danger" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }))}>×</button>
                )}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} style={{ textAlign: 'right' }}>Усього:</td>
              <td style={{ textAlign: 'right' }}>{fmt2(total)}</td>
              <td />
            </tr>
          </tfoot>
        </TchTable>
      </>)}

      {tab === 'accounts' && (
        <TchTable>
          <thead>
            <tr><th style={{ width: 40 }}>#</th><th>Працівник</th><th style={{ width: 200 }}>Рахунок розрахунків (Дт)</th><th style={{ width: 120 }}>Кт</th></tr>
          </thead>
          <tbody>
            {form.items.map((it, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{empName.get(it.employee_id) ?? '—'}</td>
                <td>
                  <input value={it.settlement_account_code} placeholder="авто: 661" disabled={isPosted}
                    onChange={(e) => updRow(i, { settlement_account_code: e.target.value })} />
                </td>
                <td>662</td>
              </tr>
            ))}
          </tbody>
        </TchTable>
      )}

      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment} disabled={isPosted} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
        </div>
        <div>
          <AttachmentsPanel organizationId={form.organization_id} documentType="doc.salary_deposits" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}
